import { useEffect, useMemo, useState } from "react";
import InviteUserModal from "../components/InviteUserModal";
import RoleAssignmentModal from "../components/RoleAssignmentModal";
import {
  inviteTeamUser,
  listTeamUsers,
  updateTeamUserRole,
  setTeamUserStatus,
} from "../services/teamService";

function getInitials(email) {
  return email
    .split("@")[0]
    .split(/[._-]/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export default function TeamUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Team members come from the API (tenant-scoped); nothing is kept locally.
  useEffect(() => {
    let active = true;
    listTeamUsers()
      .then((members) => { if (active) setUsers(members); })
      .catch((error) => { if (active) setPageError(error.message || "Unable to load team members."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  // Invite modal state
  const [showInvite, setShowInvite] = useState(false);

  // Role assignment modal state
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);

  // Page-level error state
  const [pageError, setPageError] = useState("");
  const [deactivationUser, setDeactivationUser] = useState(null);
  const [deactivationLoading, setDeactivationLoading] = useState(false);
  const [deactivationError, setDeactivationError] = useState("");

  // Search users
  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return users;
    }

    return users.filter((user) =>
      [user.name, user.email, user.role, user.status]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [users, search]);

  // Invite user
  const handleInvite = async (payload) => {
    const invitedUser = await inviteTeamUser(payload);

    setUsers((currentUsers) => [invitedUser, ...currentUsers]);

    return invitedUser;
  };

  // Open role assignment modal
  const openRoleModal = (user) => {
    setPageError("");
    setSelectedUser(user);
    setShowRoleModal(true);
  };

  // Close role assignment modal
  const closeRoleModal = () => {
    setShowRoleModal(false);
    setSelectedUser(null);
  };

  // Save updated role
  const handleRoleSave = async (userId, newRole) => {
    setPageError("");

    const updatedUser = await updateTeamUserRole(userId, {
      role: newRole,
    });

    // Update the user role in the table
    setUsers((currentUsers) =>
      currentUsers.map((user) =>
        user.id === String(userId)
          ? {
              ...user,
              role: updatedUser.role,
            }
          : user
      )
    );

    return updatedUser;
  };

  // Open a reversible deactivation confirmation instead of deleting the record.
  const openDeactivation = (user) => {
    if (user.role === "Owner") return;
    setDeactivationError("");
    setDeactivationUser(user);
  };

  const confirmDeactivation = async () => {
    if (!deactivationUser || deactivationLoading) return;
    const nextStatus = deactivationUser.status === "Deactivated" ? "Active" : "Deactivated";
    setDeactivationLoading(true);
    setDeactivationError("");
    try {
      const result = await setTeamUserStatus(deactivationUser.id, { status: nextStatus });
      setUsers((currentUsers) => currentUsers.map((user) =>
        user.id === deactivationUser.id ? { ...user, status: result.status } : user
      ));
      setDeactivationUser(null);
    } catch (error) {
      setDeactivationError(error?.message || "Unable to update user status.");
    } finally {
      setDeactivationLoading(false);
    }
  };


  return (
    <div className="team-users-page">
      {/* Page heading */}
      <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
        <div>
          <div className="text-primary small fw-semibold mb-1">
            TEAM &amp; INTEGRATIONS / ACCESS
          </div>

          <h2 className="mb-1">Team users</h2>

          <p className="text-muted mb-0">
            Manage workspace members, roles and invitations.
          </p>
        </div>

        <button
          type="button"
          className="btn btn-dark"
          onClick={() => setShowInvite(true)}
        >
          <i className="bi bi-plus-lg me-2" />
          Invite user
        </button>
      </div>

      {/* Page error */}
      {pageError && (
        <div className="alert alert-danger" role="alert">
          {pageError}
        </div>
      )}

      {/* Search and member count */}
      <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
        <div className="team-search">
          <i className="bi bi-search text-muted" />

          <input
            type="search"
            className="form-control"
            placeholder="Search users..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search team users"
          />
        </div>

        <span className="small text-muted">
          {users.length} {users.length === 1 ? "member" : "members"}
        </span>
      </div>

      {/* Users table */}
      <div className="card team-table-card">
        {loading ? (
          <div className="p-4 text-muted" role="status">Loading team members…</div>
        ) : filteredUsers.length > 0 ? (
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th scope="col">USER</th>
                  <th scope="col">ROLE</th>
                  <th scope="col">STATUS</th>
                  <th scope="col">LAST ACTIVE</th>
                  <th scope="col" className="text-end">
                    ACTIONS
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    {/* User information */}
                    <td>
                      <div className="d-flex align-items-center gap-3">
                        <div className="team-avatar">
                          {getInitials(user.email)}
                        </div>

                        <div>
                          <div className="fw-semibold">
                            {user.name}
                          </div>

                          <div className="small text-muted">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Role */}
                    <td>
                      <span className="badge text-bg-light border fw-normal">
                        {user.role}
                      </span>
                    </td>

                    {/* Status */}
                    <td>
                      <span
                        className={`team-status ${
                          user.status === "Active" ? "team-status-active" :
                          user.status === "Deactivated" ? "team-status-deactivated" : "team-status-invited"
                        }`}
                      >
                        {user.status}
                      </span>
                      {user.status === "Invited" && user.inviteCode && (
                        <div className="small text-muted mt-1">Invite code: <code>{user.inviteCode}</code></div>
                      )}
                    </td>

                    {/* Last active */}
                    <td className="text-muted">
                      {user.lastActive}
                    </td>

                    {/* Actions */}
                    <td className="text-end">
                      {user.role === "Owner" ? (
                        <span
                          className="badge text-bg-light border fw-normal"
                          title="Owner role is protected"
                        >
                          <i className="bi bi-shield-lock me-1" />
                          Protected
                        </span>
                      ) : (
                        <div className="d-flex justify-content-end gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openRoleModal(user)}
                            title={`Change role for ${user.email}`}
                          >
                            <i className="bi bi-person-gear me-1" />
                            Change role
                          </button>

                          <button
                            type="button"
                            className={`btn btn-sm ${user.status === "Deactivated" ? "btn-outline-success" : "btn-outline-danger"}`}
                            onClick={() => openDeactivation(user)}
                            title={`${user.status === "Deactivated" ? "Reactivate" : "Deactivate"} ${user.email}`}
                          >
                            <i className={`bi ${user.status === "Deactivated" ? "bi-person-check" : "bi-person-x"} me-1`} />
                            {user.status === "Deactivated" ? "Reactivate" : "Deactivate"}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          /* Empty states */
          <div className="team-empty-state">
            <div className="team-empty-icon">
              <i className="bi bi-people" />
            </div>

            {users.length === 0 ? (
              <>
                <h5>No team members yet</h5>

                <p className="text-muted">
                  Invite a user to start building your team.
                </p>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowInvite(true)}
                >
                  <i className="bi bi-plus-lg me-2" />
                  Invite user
                </button>
              </>
            ) : (
              <>
                <h5>No users found</h5>

                <p className="text-muted">
                  Try another name, email address, role or status.
                </p>

                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setSearch("")}
                >
                  Clear search
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {deactivationUser && (
        <div className="invite-modal-backdrop" role="presentation">
          <div className="invite-modal-card" role="dialog" aria-modal="true" aria-labelledby="deactivateMemberTitle">
            <div className="d-flex justify-content-between align-items-start border-bottom p-4">
              <div>
                <h5 className="mb-1" id="deactivateMemberTitle">
                  {deactivationUser.status === "Deactivated" ? "Reactivate team member" : "Deactivate team member"}
                </h5>
                <p className="text-muted small mb-0">{deactivationUser.email}</p>
              </div>
              <button type="button" className="btn-close" aria-label="Close" disabled={deactivationLoading} onClick={() => setDeactivationUser(null)} />
            </div>
            <div className="p-4">
              <p className="mb-0">{deactivationUser.status === "Deactivated"
                ? "This member will regain access to the workspace."
                : "This member will lose workspace access. Their record will remain available for audit history."}</p>
              {deactivationError && <div className="alert alert-danger mt-3 mb-0" role="alert">{deactivationError}</div>}
            </div>
            <div className="d-flex justify-content-end gap-2 border-top p-3">
              <button type="button" className="btn btn-light border" disabled={deactivationLoading} onClick={() => setDeactivationUser(null)}>Cancel</button>
              <button type="button" className={`btn ${deactivationUser.status === "Deactivated" ? "btn-success" : "btn-danger"}`} disabled={deactivationLoading} onClick={confirmDeactivation}>
                {deactivationLoading ? <><span className="spinner-border spinner-border-sm me-2" />Saving...</> : deactivationUser.status === "Deactivated" ? "Confirm reactivation" : "Confirm deactivation"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite user modal */}
      <InviteUserModal
        show={showInvite}
        onClose={() => setShowInvite(false)}
        onInvite={handleInvite}
        existingEmails={users.map((user) => user.email)}
      />

      {/* Role assignment modal */}
      <RoleAssignmentModal
        show={showRoleModal}
        user={selectedUser}
        onClose={closeRoleModal}
        onSave={handleRoleSave}
      />
    </div>
  );
}